/**
 * Voice AI — Payment Settings Tab
 * Configures AI payment behavior: mode, amount explanation, fees, SMS template, test mode.
 */
import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { settingsService } from '../../services/settings-service.js';

export class PaymentSettings extends LitElement {
  static properties = {
    _mode: { state: true },
    _explainAmount: { state: true },
    _explainFees: { state: true },
    _requireEmail: { state: true },
    _autoSendLink: { state: true },
    _smsTemplate: { state: true },
    _testMode: { state: true },
    _saving: { state: true },
    _loaded: { state: true },
    // Read-only payment summary from RVBT
    _paymentSummary: { state: true },
    _summaryLoading: { state: true },
    // Test mode details
    _testModeDetails: { state: true },
    _togglingTestMode: { state: true },
    // Gateway picker for charge_card mode
    _chargeGatewayId: { state: true },
    _allGateways: { state: true },
    _gwDropdownOpen: { state: true }
  };

  static styles = [designSystem, componentStyles, settingsStyles, css`
    /* ─── Radio Card Group ─── */
    .radio-group {
      display: flex;
      flex-direction: column;
      gap: var(--space-3);
    }
    .radio-card {
      display: flex;
      align-items: flex-start;
      gap: var(--space-3);
      padding: var(--space-4);
      background: var(--color-bg-tertiary);
      border-radius: var(--radius-xl);
      border: 2px solid var(--color-border-subtle);
      cursor: pointer;
      transition: all var(--transition-base);
    }
    .radio-card:hover {
      border-color: var(--color-border-primary);
      box-shadow: var(--shadow-sm);
    }
    .radio-card.selected {
      border-color: var(--color-primary);
      background: var(--color-primary-light);
    }
    .radio-dot {
      width: 24px;
      height: 24px;
      border-radius: var(--radius-full);
      border: 2px solid var(--color-border-secondary);
      flex-shrink: 0;
      margin-top: 2px;
      position: relative;
      transition: all var(--transition-fast);
    }
    .radio-card.selected .radio-dot {
      border-color: var(--color-primary);
    }
    .radio-card.selected .radio-dot::after {
      content: '';
      position: absolute;
      top: 4px;
      left: 4px;
      width: 12px;
      height: 12px;
      border-radius: var(--radius-full);
      background: var(--color-primary);
    }
    .radio-card-content {
      flex: 1;
    }
    .radio-card-label {
      font-family: var(--font-body);
      font-size: var(--text-sm);
      font-weight: var(--font-semibold);
      color: var(--color-text-primary);
      margin-bottom: 2px;
    }
    .radio-card-desc {
      font-family: var(--font-body);
      font-size: var(--text-xs);
      color: var(--color-text-tertiary);
      line-height: var(--leading-relaxed);
    }
    .radio-card-badge {
      font-size: var(--text-xs);
      font-weight: var(--font-bold);
      color: var(--color-primary);
      background: var(--color-primary-subtle);
      padding: 2px 8px;
      border-radius: var(--radius-full);
      margin-left: var(--space-2);
    }

    /* ─── SMS Template ─── */
    .template-textarea {
      width: 100%;
      min-height: 80px;
      padding: var(--space-3);
      background: var(--color-bg-secondary);
      border: 1px solid var(--color-border-primary);
      border-radius: var(--radius-md);
      color: var(--color-text-primary);
      font-family: var(--font-mono);
      font-size: var(--text-xs);
      line-height: var(--leading-relaxed);
      resize: vertical;
      box-sizing: border-box;
    }
    .template-textarea:focus {
      outline: none;
      border-color: var(--color-primary);
      box-shadow: var(--shadow-glow-primary);
    }
    .template-vars {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-1);
      margin-top: var(--space-2);
    }
    .template-var {
      font-family: var(--font-mono);
      font-size: 10px;
      background: var(--color-bg-inset);
      color: var(--color-text-tertiary);
      padding: 2px 6px;
      border-radius: var(--radius-sm);
      cursor: pointer;
      transition: all var(--transition-fast);
    }
    .template-var:hover {
      background: var(--color-primary-light);
      color: var(--color-primary);
    }

    /* ─── Summary Card ─── */
    .summary-card {
      background: var(--color-bg-tertiary);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-lg);
      padding: var(--space-4);
    }
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: var(--space-3);
    }
    @media (max-width: 600px) {
      .summary-grid { grid-template-columns: 1fr; }
    }
    .summary-item {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .summary-label {
      font-family: var(--font-body);
      font-size: var(--text-xs);
      color: var(--color-text-tertiary);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .summary-value {
      font-family: var(--font-body);
      font-size: var(--text-sm);
      font-weight: var(--font-semibold);
      color: var(--color-text-primary);
    }

    /* ─── Test Mode Banner ─── */
    .test-mode-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--space-4);
      border-radius: var(--radius-lg);
      margin-bottom: var(--space-4);
      transition: all var(--transition-base);
    }
    .test-mode-banner.live {
      background: rgba(45, 143, 111, 0.08);
      border: 1px solid rgba(45, 143, 111, 0.25);
    }
    .test-mode-banner.test {
      background: rgba(245, 158, 11, 0.08);
      border: 1px solid rgba(245, 158, 11, 0.25);
    }
    .test-mode-info {
      display: flex;
      align-items: center;
      gap: var(--space-3);
    }
    .test-mode-icon {
      font-size: var(--text-lg);
    }
    .test-mode-label {
      font-family: var(--font-body);
      font-size: var(--text-sm);
      font-weight: var(--font-semibold);
    }
    .test-mode-hint {
      font-family: var(--font-body);
      font-size: var(--text-xs);
      color: var(--color-text-tertiary);
    }
    .test-mode-banner.live .test-mode-icon,
    .test-mode-banner.live .test-mode-label { color: var(--color-success); }
    .test-mode-banner.test .test-mode-icon,
    .test-mode-banner.test .test-mode-label { color: var(--color-warning); }

    /* ─── Gateway Dropdown ─── */
    .gateway-picker-wrapper {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.35s ease, opacity 0.25s ease;
      opacity: 0;
    }
    .gateway-picker-wrapper.open {
      max-height: 600px;
      opacity: 1;
    }
    .gateway-picker {
      margin-top: var(--space-3);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-lg);
      padding: var(--space-3);
      background: var(--color-bg-secondary);
    }
    .gateway-picker-title {
      font-family: var(--font-body);
      font-size: var(--text-xs);
      font-weight: var(--font-semibold);
      color: var(--color-text-tertiary);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: var(--space-2);
    }

    /* Custom dropdown trigger */
    .gw-dropdown-trigger {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      padding: var(--space-3) var(--space-4);
      background: var(--color-bg-tertiary);
      border: 1px solid var(--color-border-primary);
      border-radius: var(--radius-md);
      cursor: pointer;
      transition: all var(--transition-fast);
      position: relative;
    }
    .gw-dropdown-trigger:hover {
      border-color: var(--color-primary);
    }
    .gw-dropdown-trigger.open {
      border-color: var(--color-primary);
      box-shadow: var(--shadow-glow-primary);
      border-radius: var(--radius-md) var(--radius-md) 0 0;
    }
    .gw-dropdown-trigger .gw-chevron {
      margin-left: auto;
      font-size: var(--text-xs);
      color: var(--color-text-tertiary);
      transition: transform var(--transition-fast);
    }
    .gw-dropdown-trigger.open .gw-chevron {
      transform: rotate(180deg);
    }

    /* Dropdown list */
    .gw-dropdown-list {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.25s ease;
      border: none;
      border-radius: 0 0 var(--radius-md) var(--radius-md);
      background: var(--color-bg-tertiary);
    }
    .gw-dropdown-list.open {
      border: 1px solid var(--color-border-primary);
      border-top: none;
      max-height: 260px;
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: var(--color-border-secondary) transparent;
    }
    .gw-dropdown-list.open::-webkit-scrollbar {
      width: 6px;
    }
    .gw-dropdown-list.open::-webkit-scrollbar-track {
      background: transparent;
    }
    .gw-dropdown-list.open::-webkit-scrollbar-thumb {
      background: var(--color-border-secondary);
      border-radius: 3px;
    }
    .gw-dropdown-list.open::-webkit-scrollbar-thumb:hover {
      background: var(--color-text-tertiary);
    }

    /* Gateway item (shared for trigger preview & list items) */
    .gw-item {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      padding: var(--space-3) var(--space-4);
      cursor: pointer;
      transition: all var(--transition-fast);
    }
    .gw-item:hover {
      background: var(--color-bg-inset);
    }
    .gw-item.selected {
      background: var(--color-primary-light);
    }
    .gw-item + .gw-item {
      border-top: 1px solid var(--color-border-subtle);
    }

    /* Gateway icon */
    .gw-icon {
      width: 32px;
      height: 32px;
      border-radius: var(--radius-md);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 14px;
      font-weight: var(--font-bold);
      color: #fff;
    }
    .gw-icon.stripe      { background: #635bff; }
    .gw-icon.authorizenet { background: #1d4f91; }
    .gw-icon.square       { background: #006aff; }
    .gw-icon.paypal       { background: #003087; }
    .gw-icon.braintree    { background: #000000; }
    .gw-icon.adyen        { background: #0abf53; }
    .gw-icon.worldpay     { background: #eb001b; }
    .gw-icon.clearview    { background: #2e7d32; }
    .gw-icon.checkout     { background: #0b5cff; }
    .gw-icon.mollie       { background: #1f2024; }
    .gw-icon.default      { background: var(--color-text-tertiary); }

    .gw-info {
      flex: 1;
      min-width: 0;
    }
    .gw-name {
      font-family: var(--font-body);
      font-size: var(--text-sm);
      font-weight: var(--font-semibold);
      color: var(--color-text-primary);
    }
    .gw-detail {
      font-family: var(--font-body);
      font-size: var(--text-xs);
      color: var(--color-text-tertiary);
      display: flex;
      align-items: center;
      gap: var(--space-2);
      flex-wrap: wrap;
    }
  `];

  constructor() {
    super();
    this._mode = 'sms_link';
    this._explainAmount = true;
    this._explainFees = true;
    this._requireEmail = false;
    this._autoSendLink = true;
    this._smsTemplate = '';
    this._testMode = false;
    this._saving = false;
    this._loaded = false;
    this._paymentSummary = null;
    this._summaryLoading = true;
    this._testModeDetails = null;
    this._togglingTestMode = false;
    this._chargeGatewayId = '';
    this._allGateways = [];
    this._gwDropdownOpen = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
    this._loadSummary();
    this._onDocClick = (e) => {
      if (this._gwDropdownOpen && !e.composedPath().some(el => el.classList?.contains('gw-dropdown-trigger') || el.classList?.contains('gw-dropdown-list'))) {
        this._gwDropdownOpen = false;
      }
    };
    document.addEventListener('click', this._onDocClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick, true);
  }

  async _load() {
    try {
      const data = await settingsService.loadPaymentConfig();
      if (data.success && data.config) {
        const c = data.config;
        this._mode = c.voice_payment_mode || 'sms_link';
        this._explainAmount = c.voice_payment_explain_amount !== '0';
        this._explainFees = c.voice_payment_explain_fees !== '0';
        this._requireEmail = c.voice_payment_require_email === '1';
        this._autoSendLink = c.voice_payment_auto_send_link !== '0';
        this._smsTemplate = c.voice_payment_sms_template || '';
        this._testMode = c.voice_payment_test_mode === '1';
        this._chargeGatewayId = c.voice_payment_charge_gateway_id || '';
        this._loaded = true;
      }
      if (data.test_mode) {
        this._testModeDetails = data.test_mode;
      }
    } catch (err) {
      console.error('[PaymentSettings] Load failed:', err);
    }
  }

  async _loadSummary() {
    try {
      this._summaryLoading = true;
      const data = await settingsService.loadPaymentCalculation();
      if (data.success) {
        this._paymentSummary = data;
        this._allGateways = data.all_gateways || [];
        this._autoSelectGateway();
      }
    } catch (err) {
      console.error('[PaymentSettings] Summary load failed:', err);
    } finally {
      this._summaryLoading = false;
    }
  }

  /** Auto-select gateway: use saved value, else active (published), else first */
  _autoSelectGateway() {
    if (this._allGateways.length === 0) return;
    // If saved ID matches a gateway, keep it
    if (this._chargeGatewayId && this._allGateways.some(gw => String(gw.id) === String(this._chargeGatewayId))) return;
    // Pick the published (active) gateway, or fall back to first
    const active = this._allGateways.find(gw => gw.published);
    this._chargeGatewayId = String((active || this._allGateways[0]).id);
  }

  async _toggleTestMode() {
    if (this._togglingTestMode) return;
    this._togglingTestMode = true;
    try {
      const newVal = !this._testMode;
      const data = await settingsService.setTestMode(newVal);
      if (data.success) {
        this._testMode = data.voice_test_mode;
        this._testModeDetails = data;
        this.dispatchEvent(new CustomEvent('toast', {
          detail: { message: `Test mode ${newVal ? 'enabled' : 'disabled'}`, type: 'success' },
          bubbles: true, composed: true
        }));
      } else {
        this.dispatchEvent(new CustomEvent('toast', {
          detail: { message: data.error || 'Failed to toggle test mode', type: 'error' },
          bubbles: true, composed: true
        }));
      }
    } catch (err) {
      this.dispatchEvent(new CustomEvent('toast', {
        detail: { message: 'Network error toggling test mode', type: 'error' },
        bubbles: true, composed: true
      }));
    } finally {
      this._togglingTestMode = false;
    }
  }

  async _save() {
    if (this._saving) return;
    this._saving = true;

    try {
      const payload = {
        voice_payment_mode: this._mode,
        voice_payment_explain_amount: this._explainAmount ? '1' : '0',
        voice_payment_explain_fees: this._explainFees ? '1' : '0',
        voice_payment_require_email: this._requireEmail ? '1' : '0',
        voice_payment_auto_send_link: this._autoSendLink ? '1' : '0',
        voice_payment_sms_template: this._smsTemplate,
        voice_payment_test_mode: this._testMode ? '1' : '0',
        voice_payment_charge_gateway_id: this._chargeGatewayId
      };

      const data = await settingsService.savePaymentConfig(payload);
      if (data.success) {
        this.dispatchEvent(new CustomEvent('toast', {
          detail: { message: data.message || 'Payment settings saved', type: 'success' },
          bubbles: true, composed: true
        }));
      } else {
        this.dispatchEvent(new CustomEvent('toast', {
          detail: { message: data.error || 'Save failed', type: 'error' },
          bubbles: true, composed: true
        }));
      }
    } catch (err) {
      this.dispatchEvent(new CustomEvent('toast', {
        detail: { message: 'Network error saving settings', type: 'error' },
        bubbles: true, composed: true
      }));
    } finally {
      this._saving = false;
    }
  }

  _getDefaultTemplate() {
    if (this._mode === 'guest_portal') {
      return 'Hi {guest_name}, access your reservation and complete payment here: {guest_portal_url}';
    }
    return 'Hi {guest_name}, complete your reservation payment of ${amount}: {payment_link}';
  }

  _insertVar(v) {
    this._smsTemplate = (this._smsTemplate || '') + `{${v}}`;
  }

  render() {
    // Voice test mode is the sole authority — gateway sandbox is irrelevant
    const effectiveMode = this._testMode ? 'test' : 'live';

    return html`
      ${iconStyles}
      <div class="content-header">
        <h2 class="content-title">Payment Settings</h2>
        <p class="content-description">Configure how the AI handles payments after creating reservations. Controls what information the AI shares and how payment links are sent.</p>
      </div>

      ${!this._loaded ? html`
        <!-- ─── Skeleton: Payment Mode ─── -->
        <div class="settings-section">
          <div class="section-title-row">
            <i class="fa-sharp-duotone fa-solid fa-credit-card section-icon"></i>
            <span class="section-title">Payment Mode</span>
          </div>
          <div class="skeleton-line medium sm" style="margin-bottom:var(--space-4);"></div>
          <div style="display:flex;flex-direction:column;gap:var(--space-3);">
            ${[1,2,3,4].map(() => html`
              <div class="skeleton-card" style="height:64px;padding:var(--space-4);display:flex;align-items:center;gap:var(--space-3);">
                <div style="width:20px;height:20px;border-radius:50%;background:var(--color-border-subtle);flex-shrink:0;"></div>
                <div style="flex:1;display:flex;flex-direction:column;gap:6px;">
                  <div class="skeleton-line short sm"></div>
                  <div class="skeleton-line long xs"></div>
                </div>
              </div>
            `)}
          </div>
        </div>

        <!-- ─── Skeleton: AI Behavior ─── -->
        <div class="settings-section">
          <div class="section-title-row">
            <i class="fa-sharp-duotone fa-solid fa-message-bot section-icon"></i>
            <span class="section-title">AI Conversation Behavior</span>
          </div>
          <div class="skeleton-line medium sm" style="margin-bottom:var(--space-3);"></div>
          ${[1,2,3,4].map(() => html`
            <div class="skeleton-row">
              <div style="flex:1;display:flex;flex-direction:column;gap:4px;">
                <div class="skeleton-line short sm"></div>
                <div class="skeleton-line long xs"></div>
              </div>
              <div class="skeleton-toggle"></div>
            </div>
          `)}
        </div>

        <!-- ─── Skeleton: SMS Template ─── -->
        <div class="settings-section">
          <div class="section-title-row">
            <i class="fa-sharp-duotone fa-solid fa-message-sms section-icon"></i>
            <span class="section-title">SMS Message Template</span>
          </div>
          <div class="skeleton-line medium sm" style="margin-bottom:var(--space-3);"></div>
          <div class="skeleton-card" style="height:80px;"></div>
        </div>
      ` : html`
        <!-- ─── Section 1: Payment Mode ─── -->
        <div class="settings-section">
          <div class="section-title-row">
            <i class="fa-sharp-duotone fa-solid fa-credit-card section-icon"></i>
            <span class="section-title">Payment Mode</span>
          </div>
          <p class="section-description">What should the AI do after creating a reservation?</p>

          <div class="radio-group">
            ${this._renderModeCard('sms_link', 'Send SMS Payment Link', 'Send a secure, time-limited payment link to the caller via SMS. The guest clicks the link to complete payment online.', true)}
            ${this._renderModeCard('guest_portal', 'Send Guest Portal URL', 'Send the guest portal URL via SMS where the caller can view their reservation and complete payment.')}
            ${this._renderModeCard('inform_only', 'Inform Only', 'Tell the caller the amount due but do not send any payment link or URL. Payment must be completed separately.')}
            ${this._renderModeCard('charge_card', 'Payment Module', 'Use a specific payment module to charge cards on file. Select the module below.')}
          </div>

          <!-- Gateway dropdown (expands when charge_card selected) -->
          <div class="gateway-picker-wrapper ${this._mode === 'charge_card' ? 'open' : ''}">
            <div class="gateway-picker">
              <div class="gateway-picker-title">Select Payment Module</div>
              ${this._allGateways.length > 0 ? html`
                ${this._renderGatewayDropdown()}
              ` : html`
                <div class="empty-hint" style="padding:var(--space-3);">
                  <i class="fa-sharp fa-light fa-credit-card-blank"></i>
                  No payment modules found
                </div>
              `}
            </div>
          </div>
        </div>

        <!-- ─── Section 2: AI Conversation Behavior ─── -->
        <div class="settings-section">
          <div class="section-title-row">
            <i class="fa-sharp-duotone fa-solid fa-message-bot section-icon"></i>
            <span class="section-title">AI Conversation Behavior</span>
          </div>
          <p class="section-description">Control what the AI tells the caller about payment details.</p>

          <div class="feature-list">
            ${this._renderFeature('explainAmount', 'Explain Deposit vs Full Amount', 'AI will tell the caller whether a deposit or full payment is required, and state both amounts.', 'fa-money-check-dollar', 'var(--color-success)')}
            ${this._renderFeature('explainFees', 'Mention Booking Fees', 'AI will inform the caller about any booking fees that apply to their reservation.', 'fa-receipt', 'var(--color-warning)')}
            ${this._renderFeature('requireEmail', 'Require Email Before Booking', 'AI must collect the caller\'s email address before creating a reservation.', 'fa-envelope', 'var(--color-info)')}
            ${this._renderFeature('autoSendLink', 'Auto-Send Payment Link', 'AI automatically sends the payment link after booking without asking. If off, AI asks the caller first.', 'fa-paper-plane', 'var(--color-accent)')}
          </div>
        </div>

        <!-- ─── Section 3: SMS Template ─── -->
        <div class="settings-section">
          <div class="section-title-row">
            <i class="fa-sharp-duotone fa-solid fa-message-sms section-icon"></i>
            <span class="section-title">SMS Message Template</span>
          </div>
          <p class="section-description">Customize the SMS message sent to callers. Leave blank to use the default template.</p>

          <div class="form-group">
            <label class="form-label">Message Template</label>
            <textarea
              class="template-textarea"
              .value=${this._smsTemplate}
              @input=${e => this._smsTemplate = e.target.value}
              placeholder="${this._getDefaultTemplate()}"
            ></textarea>
            <div class="template-vars">
              <span class="form-hint" style="margin-right:var(--space-2)">Variables:</span>
              ${['payment_link', 'guest_portal_url', 'amount', 'deposit_amount', 'confirmation_number', 'guest_name'].map(v => html`
                <span class="template-var" @click=${() => this._insertVar(v)}>{${v}}</span>
              `)}
            </div>
          </div>
        </div>
      `}

      <!-- ─── Section 4: Payment Summary ─── -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-chart-pie section-icon"></i>
          <span class="section-title">RVBT Payment Configuration</span>
        </div>
        <p class="section-description">Current payment settings from your RVBT system. The AI uses these to calculate amounts accurately.</p>

        ${this._summaryLoading ? html`
          <div class="empty-hint">
            <i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 0.6s linear infinite;"></i>
            Loading payment configuration...
          </div>
        ` : this._paymentSummary ? html`
          <div class="summary-card">
            <div class="summary-grid">
              <div class="summary-item">
                <span class="summary-label">Payment Type</span>
                <span class="summary-value">${this._paymentSummary.deposit_settings?.pay_total ? 'Full Payment Required' : 'Deposit Allowed'}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Deposit Amount</span>
                <span class="summary-value">${this._paymentSummary.deposit_settings?.pay_total ? 'N/A' :
                  this._paymentSummary.deposit_settings?.deposit_type === 'fixed'
                    ? '$' + this._paymentSummary.deposit_settings?.deposit_amount
                    : this._paymentSummary.deposit_settings?.deposit_amount + '%'
                }</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Gateway</span>
                <span class="summary-value">${this._getActiveGateway()?.name || this._paymentSummary.gateway?.name || 'Not configured'}${(() => {
                  const gw = this._getActiveGateway() || this._paymentSummary.gateway;
                  return gw?.type ? html` <span style="opacity:0.6;font-weight:var(--font-normal);">(${gw.type})</span>` : '';
                })()}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Currency</span>
                <span class="summary-value">${((this._getActiveGateway() || this._paymentSummary.gateway)?.currency || 'USD').toUpperCase()}</span>
              </div>
              ${(() => {
                const gw = this._getActiveGateway() || this._paymentSummary.gateway;
                return gw?.charge > 0 && gw?.charge_enabled ? html`
                  <div class="summary-item">
                    <span class="summary-label">${gw.ch_disc === 1 || gw.ch_disc === undefined ? 'Transaction Fee' : 'Transaction Discount'}</span>
                    <span class="summary-value">${gw.val_pcent === 2 ? gw.charge + '%' : '$' + Number(gw.charge).toFixed(2)}</span>
                  </div>
                ` : '';
              })()}
              ${this._paymentSummary.fees?.length > 0 ? html`
                <div class="summary-item">
                  <span class="summary-label">Active Booking Fees</span>
                  <span class="summary-value">${this._paymentSummary.fees.length} fee(s) configured</span>
                </div>
              ` : ''}
              <div class="summary-item">
                <span class="summary-label">Advance Days Rule</span>
                <span class="summary-value">${this._paymentSummary.deposit_settings?.deposit_days_advance > 0
                  ? 'Full payment if within ' + this._paymentSummary.deposit_settings.deposit_days_advance + ' days'
                  : 'No restriction'
                }</span>
              </div>
            </div>
          </div>
        ` : html`
          <div class="empty-hint">
            <i class="fa-sharp fa-light fa-triangle-exclamation"></i>
            Could not load payment configuration
          </div>
        `}
      </div>

      <!-- ─── Section 5: Test Mode ─── -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-flask-vial section-icon"></i>
          <span class="section-title">Test Mode</span>
        </div>
        <p class="section-description">Toggle test/sandbox mode for payment processing. No real charges in test mode.</p>

        <div class="test-mode-banner ${effectiveMode}">
          <div class="test-mode-info">
            <i class="fa-sharp fa-light ${effectiveMode === 'test' ? 'fa-flask-vial' : 'fa-shield-check'} test-mode-icon"></i>
            <div>
              <div class="test-mode-label">${effectiveMode === 'test' ? 'TEST MODE' : 'LIVE MODE'}</div>
              <div class="test-mode-hint">${effectiveMode === 'test'
                ? 'Using sandbox keys. No real charges will be processed.'
                : 'Using live keys. Real charges will be processed.'
              }</div>
            </div>
          </div>
          <div
            class="toggle ${this._testMode ? 'active' : ''}"
            @click=${this._toggleTestMode}
            style="${this._togglingTestMode ? 'opacity:0.5;pointer-events:none;' : ''}"
          ></div>
        </div>

        ${this._testModeDetails?.gateway_sandbox ? html`
          <div class="form-hint" style="color:var(--color-text-tertiary);">
            <i class="fa-sharp fa-light fa-circle-info"></i>
            Note: Your RVBT gateway is in ${this._testModeDetails.gateway_sandbox ? 'sandbox' : 'live'} mode, but Voice AI always uses its own test mode setting above.
          </div>
        ` : ''}
      </div>

      <!-- ─── Save Button ─── -->
      ${this._loaded ? html`
        <div class="actions-bar">
          <button class="btn btn-primary" @click=${this._save} ?disabled=${this._saving}>
            ${this._saving
              ? html`<i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 0.6s linear infinite;"></i> Saving...`
              : html`<i class="fa-sharp fa-light fa-floppy-disk"></i> Save Changes`
            }
          </button>
        </div>
      ` : ''}
    `;
  }

  _renderModeCard(key, label, desc, recommended = false) {
    return html`
      <div class="radio-card ${this._mode === key ? 'selected' : ''}" @click=${() => { this._mode = key; }}>
        <div class="radio-dot"></div>
        <div class="radio-card-content">
          <div class="radio-card-label">
            ${label}
            ${recommended ? html`<span class="radio-card-badge">Recommended</span>` : ''}
          </div>
          <div class="radio-card-desc">${desc}</div>
        </div>
      </div>
    `;
  }

  /** When charge_card mode + gateway selected, return that gateway from _allGateways */
  _getActiveGateway() {
    if (this._mode === 'charge_card' && this._chargeGatewayId && this._allGateways.length > 0) {
      return this._allGateways.find(gw => String(gw.id) === String(this._chargeGatewayId)) || null;
    }
    return null;
  }

  /** Gateway icon mapping — letter + CSS class by gateway file/type */
  _getGatewayIcon(gw) {
    const file = (gw.file || gw.type || '').toLowerCase();
    const map = {
      stripe:        { letter: 'S', cls: 'stripe' },
      authorizenet:  { letter: 'A', cls: 'authorizenet' },
      square:        { letter: 'Sq', cls: 'square' },
      paypal:        { letter: 'P', cls: 'paypal' },
      braintree:     { letter: 'B', cls: 'braintree' },
      adyen:         { letter: 'Ad', cls: 'adyen' },
      worldpay:      { letter: 'W', cls: 'worldpay' },
      clearview:     { letter: 'C', cls: 'clearview' },
      checkout:      { letter: 'Ch', cls: 'checkout' },
      mollie:        { letter: 'M', cls: 'mollie' },
      offline_credit_card: { letter: 'CC', cls: 'default' }
    };
    // Try exact match, then partial match
    if (map[file]) return map[file];
    for (const [k, v] of Object.entries(map)) {
      if (file.includes(k)) return v;
    }
    // Fallback: first letter of name or type
    const letter = (gw.name || gw.file || '?').charAt(0).toUpperCase();
    return { letter, cls: 'default' };
  }

  _renderGatewayDropdown() {
    const selected = this._allGateways.find(gw => String(gw.id) === String(this._chargeGatewayId));
    const selIcon = selected ? this._getGatewayIcon(selected) : null;

    return html`
      <!-- Trigger -->
      <div
        class="gw-dropdown-trigger ${this._gwDropdownOpen ? 'open' : ''}"
        @click=${() => { this._gwDropdownOpen = !this._gwDropdownOpen; }}
      >
        ${selected ? html`
          <span class="gw-icon ${selIcon.cls}">${selIcon.letter}</span>
          <div class="gw-info">
            <div class="gw-name">${selected.name}</div>
            <div class="gw-detail">
              <span>${selected.type || selected.file || 'Unknown'}</span>
              ${this._getGatewayFeeText(selected) ? html`<span>${this._getGatewayFeeText(selected)}</span>` : ''}
            </div>
          </div>
        ` : html`
          <span style="color:var(--color-text-tertiary);font-size:var(--text-sm);">Select a payment module...</span>
        `}
        <i class="fa-sharp fa-solid fa-chevron-down gw-chevron"></i>
      </div>

      <!-- Dropdown list -->
      <div class="gw-dropdown-list ${this._gwDropdownOpen ? 'open' : ''}">
        ${this._allGateways.map(gw => {
          const icon = this._getGatewayIcon(gw);
          const isSelected = String(gw.id) === String(this._chargeGatewayId);
          const feeText = this._getGatewayFeeText(gw);
          return html`
            <div
              class="gw-item ${isSelected ? 'selected' : ''}"
              @click=${() => {
                this._chargeGatewayId = String(gw.id);
                this._gwDropdownOpen = false;
              }}
            >
              <span class="gw-icon ${icon.cls}">${icon.letter}</span>
              <div class="gw-info">
                <div class="gw-name">${gw.name}</div>
                <div class="gw-detail">
                  <span>${gw.type || gw.file || 'Unknown'}</span>
                  ${feeText ? html`<span>${feeText}</span>` : ''}
                </div>
              </div>
              ${isSelected ? html`<i class="fa-sharp fa-solid fa-check" style="color:var(--color-primary);font-size:var(--text-sm);margin-left:auto;"></i>` : ''}
            </div>
          `;
        })}
      </div>
    `;
  }

  /** Format fee text for a gateway */
  _getGatewayFeeText(gw) {
    if (!gw.charge_enabled || !gw.charge || Number(gw.charge) <= 0) return '';
    const isDiscount = gw.ch_disc === 2;
    const isPercent = gw.val_pcent === 2;
    const label = isDiscount ? 'discount' : 'fee';
    const val = isPercent ? `${gw.charge}%` : `$${Number(gw.charge).toFixed(2)}`;
    return `${val} ${label}`;
  }

  _renderFeature(key, label, desc, icon, color) {
    const propMap = {
      explainAmount: '_explainAmount',
      explainFees: '_explainFees',
      requireEmail: '_requireEmail',
      autoSendLink: '_autoSendLink'
    };
    const prop = propMap[key];
    return html`
      <div class="feature-item">
        <div class="feature-info">
          <div class="feature-label">
            <i class="fa-sharp fa-light ${icon}" style="margin-right:var(--space-2);color:${color};font-size:var(--text-xs);"></i>
            ${label}
          </div>
          <div class="feature-desc">${desc}</div>
        </div>
        <div
          class="toggle ${this[prop] ? 'active' : ''}"
          @click=${() => { this[prop] = !this[prop]; }}
        ></div>
      </div>
    `;
  }
}

customElements.define('payment-settings', PaymentSettings);
